import React from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { withTranslation } from "react-i18next";

const RING_OPTIONS = ["canary", "early", "fleet"];

const HelpCallout = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1,
      mt: 1.5,
      p: 1.5,
      bgcolor: "rgba(56, 115, 113, 0.07)",
      borderLeft: "3px solid #387371",
      borderRadius: "0 4px 4px 0",
    }}
  >
    <InfoOutlined
      sx={{ fontSize: 18, color: "#387371", mt: "1px", flexShrink: 0 }}
    />
    <Typography
      sx={{ fontSize: 13, color: "rgba(0, 0, 0, 0.72)", lineHeight: 1.55 }}
    >
      {children}
    </Typography>
  </Box>
);

HelpCallout.propTypes = {
  children: PropTypes.node.isRequired,
};

const SectionCard = ({ title, description, children }) => (
  <Box
    sx={{
      mb: 2,
      p: 2,
      border: "1px solid #e0e0e0",
      borderRadius: "6px",
      backgroundColor: "#fff",
    }}
  >
    <Typography
      sx={{
        fontSize: 15,
        fontWeight: 600,
        color: "rgba(0, 0, 0, 0.82)",
        mb: description ? 0.5 : 1.5,
      }}
    >
      {title}
    </Typography>
    {description ? (
      <Typography
        sx={{
          fontSize: 13,
          color: "rgba(0, 0, 0, 0.6)",
          lineHeight: 1.5,
          mb: 1.5,
        }}
      >
        {description}
      </Typography>
    ) : null}
    {children}
  </Box>
);

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node.isRequired,
};

const CurrentVersion = ({ label, value, emptyLabel }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography
      sx={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 12, lineHeight: 1.35, mb: 0.75 }}
    >
      {label}
    </Typography>
    <Chip
      size="small"
      label={value || emptyLabel}
      sx={{
        height: 28,
        fontSize: 13,
        fontWeight: value ? 600 : 400,
        color: value ? "rgba(0, 0, 0, 0.82)" : "rgba(0, 0, 0, 0.45)",
        bgcolor: value ? "rgba(56, 115, 113, 0.12)" : "rgba(0, 0, 0, 0.06)",
      }}
    />
  </Box>
);

CurrentVersion.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  emptyLabel: PropTypes.string.isRequired,
};

const formatLastCheck = (value) => {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const AutoUpdateSettingsTab = ({ t, readOnly, values, onChange }) => {
  const emptyLabel = t("Location.AutoUpdate.NotSet");

  return (
    <Box sx={{ p: "20px 24px 32px", maxWidth: 560 }}>
      <Alert
        severity="info"
        icon={<InfoOutlined fontSize="inherit" />}
        sx={{
          mb: 2,
          alignItems: "flex-start",
          bgcolor: "rgba(56, 115, 113, 0.08)",
          color: "rgba(0, 0, 0, 0.78)",
          "& .MuiAlert-icon": { color: "#387371" },
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 0.5 }}>
          {t("Location.AutoUpdate.IntroTitle")}
        </Typography>
        <Typography sx={{ fontSize: 13, lineHeight: 1.55 }}>
          {t("Location.AutoUpdate.IntroBody")}
        </Typography>
      </Alert>

      <SectionCard
        title={t("Location.AutoUpdate.WebSectionTitle")}
        description={t("Location.AutoUpdate.WebSectionDescription")}
      >
        <CurrentVersion
          label={t("Location.AutoUpdate.KioskCurrentVersion")}
          value={values.kiosk_version_current}
          emptyLabel={emptyLabel}
        />
        <HelpCallout>{t("Location.AutoUpdate.KioskCurrentHelp")}</HelpCallout>
        <TextField
          id="KioskTargetVersion"
          name="KioskTargetVersion"
          label={t("Location.AutoUpdate.KioskTargetVersion")}
          onChange={(event) =>
            onChange({ kiosk_version_available: event.target.value })
          }
          value={values.kiosk_version_available || ""}
          disabled={readOnly}
          variant="standard"
          fullWidth
          sx={{ mt: 2 }}
        />
        <HelpCallout>{t("Location.AutoUpdate.KioskTargetHelp")}</HelpCallout>
      </SectionCard>

      <SectionCard
        title={t("Location.AutoUpdate.NativeSectionTitle")}
        description={t("Location.AutoUpdate.NativeSectionDescription")}
      >
        <CurrentVersion
          label={t("Location.AutoUpdate.EngineCurrentVersion")}
          value={values.engine_version_current}
          emptyLabel={emptyLabel}
        />
        <TextField
          id="EngineTargetVersion"
          name="EngineTargetVersion"
          label={t("Location.AutoUpdate.EngineTargetVersion")}
          onChange={(event) =>
            onChange({ engine_version_target: event.target.value })
          }
          value={values.engine_version_target || ""}
          disabled={readOnly}
          variant="standard"
          fullWidth
        />
        <HelpCallout>{t("Location.AutoUpdate.EngineTargetHelp")}</HelpCallout>

        <Box sx={{ mt: 2.5 }}>
          <CurrentVersion
            label={t("Location.AutoUpdate.WrapperCurrentVersion")}
            value={values.wrapper_version_current}
            emptyLabel={emptyLabel}
          />
          <TextField
            id="WrapperTargetVersion"
            name="WrapperTargetVersion"
            label={t("Location.AutoUpdate.WrapperTargetVersion")}
            onChange={(event) =>
              onChange({ wrapper_version_target: event.target.value })
            }
            value={values.wrapper_version_target || ""}
            disabled={readOnly}
            variant="standard"
            fullWidth
          />
          <HelpCallout>{t("Location.AutoUpdate.WrapperTargetHelp")}</HelpCallout>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <CurrentVersion
            label={t("Location.AutoUpdate.PosBridgeCurrentVersion")}
            value={values.pos_bridge_version_current}
            emptyLabel={emptyLabel}
          />
          <TextField
            id="PosBridgeTargetVersion"
            name="PosBridgeTargetVersion"
            label={t("Location.AutoUpdate.PosBridgeTargetVersion")}
            onChange={(event) =>
              onChange({ pos_bridge_version_target: event.target.value })
            }
            value={values.pos_bridge_version_target || ""}
            disabled={readOnly}
            variant="standard"
            fullWidth
          />
          <HelpCallout>{t("Location.AutoUpdate.PosBridgeTargetHelp")}</HelpCallout>
        </Box>
      </SectionCard>

      <SectionCard
        title={t("Location.AutoUpdate.RolloutSectionTitle")}
        description={t("Location.AutoUpdate.RolloutSectionDescription")}
      >
        <FormControl variant="standard" fullWidth disabled={readOnly}>
          <InputLabel id="component-update-ring-label">
            {t("Location.AutoUpdate.RingLabel")}
          </InputLabel>
          <Select
            labelId="component-update-ring-label"
            id="ComponentUpdateRing"
            value={values.component_update_ring || "fleet"}
            onChange={(event) =>
              onChange({ component_update_ring: event.target.value })
            }
          >
            {RING_OPTIONS.map((ring) => (
              <MenuItem key={ring} value={ring}>
                {t(`Location.AutoUpdate.Ring.${ring}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <HelpCallout>{t("Location.AutoUpdate.RingHelp")}</HelpCallout>

        <Box sx={{ mt: 2 }}>
          <FormControlLabel
            label={
              <span style={{ fontSize: 16, color: "rgb(80, 80, 80)" }}>
                {t("Location.AutoUpdate.PauseLabel")}
              </span>
            }
            control={
              <Switch
                checked={Boolean(values.component_update_paused)}
                color="primary"
                onChange={(event) =>
                  onChange({ component_update_paused: event.target.checked })
                }
                disabled={readOnly}
              />
            }
          />
          <HelpCallout>{t("Location.AutoUpdate.PauseHelp")}</HelpCallout>
        </Box>
      </SectionCard>

      {values.component_update_mismatch ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("Location.AutoUpdate.MismatchHelp")}
        </Alert>
      ) : null}

      <Box
        sx={{
          px: 0.5,
          color: "rgba(0, 0, 0, 0.6)",
          fontSize: 12,
        }}
      >
        {t("Location.AutoUpdate.LastCheckLabel")}:{" "}
        {values.updater_last_check_at
          ? formatLastCheck(values.updater_last_check_at)
          : emptyLabel}
      </Box>
      <HelpCallout>{t("Location.AutoUpdate.LastCheckHelp")}</HelpCallout>
    </Box>
  );
};

AutoUpdateSettingsTab.propTypes = {
  t: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  values: PropTypes.shape({
    kiosk_version_current: PropTypes.string,
    kiosk_version_available: PropTypes.string,
    engine_version_current: PropTypes.string,
    engine_version_target: PropTypes.string,
    wrapper_version_current: PropTypes.string,
    wrapper_version_target: PropTypes.string,
    pos_bridge_version_current: PropTypes.string,
    pos_bridge_version_target: PropTypes.string,
    component_update_ring: PropTypes.string,
    component_update_paused: PropTypes.bool,
    component_update_mismatch: PropTypes.bool,
    updater_last_check_at: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

AutoUpdateSettingsTab.defaultProps = {
  readOnly: false,
};

export default withTranslation()(AutoUpdateSettingsTab);



// WEBPACK FOOTER //
// ./src/components/box-list/AutoUpdateSettingsTab.js